import { AZURE_TRANSLATOR_ENDPOINT, AZURE_TRANSLATOR_KEY, AZURE_TRANSLATOR_REGION } from "../constants/Constants";

class AzureTranslateService {
    constructor() {
        this.cache = new Map();
        this.maxCacheSize = 500;
    }

    /**
     * Get cached translation if available
     * @param {string} text - Original text
     * @param {string} targetLanguage - Target language code
     * @returns {string|null} Cached translation or null
     */
    getCachedTranslation(text, targetLanguage) {
        const cacheKey = `${text}_${targetLanguage}`;
        return this.cache.get(cacheKey) || null;
    }

    /**
     * Add translation to cache with LRU eviction
     * @param {string} text - Original text
     * @param {string} targetLanguage - Target language code
     * @param {string} translatedText - Translated text
     */
    setCachedTranslation(text, targetLanguage, translatedText) {
        const cacheKey = `${text}_${targetLanguage}`;

        // LRU eviction - remove oldest entry if cache is full
        if (this.cache.size >= this.maxCacheSize) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }

        this.cache.set(cacheKey, translatedText);
    }

    /**
     * Translate text using Azure Translator API
     * @param {string} text - Text to translate
     * @param {string} targetLanguage - Target language code (e.g., 'es', 'fr', 'de')
     * @returns {Promise<{translatedText: string, detectedLanguage: string}>}
     */
    async translateText(text, targetLanguage) {
        // Check cache first
        const cachedTranslation = this.getCachedTranslation(text, targetLanguage);
        if (cachedTranslation) {
            return {
                translatedText: cachedTranslation.translatedText,
                detectedLanguage: cachedTranslation.detectedLanguage,
                fromCache: true,
            };
        }

        // Skip translation if text is empty or too short
        if (!text || text.trim().length === 0) {
            return {
                translatedText: text,
                detectedLanguage: null,
                error: "Empty text",
            };
        }

        // Azure Translator API has a limit of 10,000 characters
        if (text.length > 10000) {
            text = text.substring(0, 10000);
        }

        // Detect source language for non-Latin scripts (Azure sometimes misdetects)
        let sourceLanguageParam = "";
        if (/[\u4e00-\u9fff]/.test(text)) {
            // Chinese characters detected
            sourceLanguageParam = "&from=zh-Hans";
        } else if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) {
            // Japanese characters detected
            sourceLanguageParam = "&from=ja";
        } else if (/[\uac00-\ud7af]/.test(text)) {
            // Korean characters detected
            sourceLanguageParam = "&from=ko";
        } else if (/[\u0600-\u06ff]/.test(text)) {
            // Arabic characters detected
            sourceLanguageParam = "&from=ar";
        } else if (/[\u0400-\u04ff]/.test(text)) {
            // Cyrillic characters detected
            sourceLanguageParam = "&from=ru";
        }

        if (sourceLanguageParam) {
            console.log("Explicit source language detected:", sourceLanguageParam);
        }

        try {
            const response = await fetch(`${AZURE_TRANSLATOR_ENDPOINT}/translate?api-version=3.0&to=${targetLanguage}${sourceLanguageParam}`, {
                method: "POST",
                headers: {
                    "Ocp-Apim-Subscription-Key": AZURE_TRANSLATOR_KEY,
                    "Ocp-Apim-Subscription-Region": AZURE_TRANSLATOR_REGION,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify([{ text }]),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Azure Translator API error:", response.status, errorText);
                throw new Error(`Translation failed: ${response.status}`);
            }

            const data = await response.json();

            if (data && data[0] && data[0].translations && data[0].translations[0]) {
                const result = {
                    translatedText: data[0].translations[0].text,
                    detectedLanguage: data[0].detectedLanguage?.language || null,
                };

                // Cache the result
                this.setCachedTranslation(text, targetLanguage, result);

                return result;
            }

            throw new Error("Invalid response format from Azure Translator");
        } catch (error) {
            console.error("Translation error:", error);
            return {
                translatedText: text,
                detectedLanguage: null,
                error: error.message,
            };
        }
    }

    /**
     * Translate a chat message
     * @param {string} message - Message to translate
     * @param {string} targetLanguage - Target language code
     * @returns {Promise<{translatedText: string, detectedLanguage: string, isTranslated: boolean}>}
     */
    async translateMessage(message, targetLanguage) {
        const result = await this.translateText(message, targetLanguage);

        // Check if text contains non-Latin characters (CJK, Arabic, Cyrillic, etc.)
        // This helps detect when Azure misidentifies the source language
        const hasNonLatinChars = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af\u0600-\u06ff\u0400-\u04ff]/.test(message);

        // Check if the detected language is the same as target (no translation needed)
        // Handle Chinese language variants (zh, zh-Hans, zh-Hant, lzh)
        const detectedLang = result.detectedLanguage?.toLowerCase() || "";
        const targetLang = targetLanguage?.toLowerCase() || "";

        // If text has non-Latin chars but Azure detected it as Latin language (en, es, fr, etc.),
        // this is likely a misdetection - proceed with translation anyway
        const isLikelyMisdetection = hasNonLatinChars && ["en", "es", "fr", "de", "it", "pt", "pl"].includes(detectedLang);

        const isSameLanguage =
            !isLikelyMisdetection && (detectedLang === targetLang || (detectedLang.startsWith("zh") && targetLang.startsWith("zh")));

        // Check if the translated text is actually different from original
        // Normalize by removing whitespace for comparison (Azure sometimes just adds spaces)
        const normalizedOriginal = message.replace(/\s+/g, "");
        const normalizedTranslated = result.translatedText.replace(/\s+/g, "");
        const textActuallyChanged = normalizedTranslated !== normalizedOriginal;

        return {
            translatedText: result.translatedText,
            detectedLanguage: result.detectedLanguage,
            isTranslated: !result.error && (!isSameLanguage || textActuallyChanged),
            error: result.error,
        };
    }

    /**
     * Clear the translation cache
     */
    clearCache() {
        this.cache.clear();
    }
}

export default new AzureTranslateService();
